package com.siriusxm.pia.views.unifiedaggregator

import androidx.compose.runtime.Composable
import androidx.compose.runtime.State
import com.siriusxm.cmc.utils.EagerLoadedState
import com.siriusxm.pia.ApplicationContext
import com.siriusxm.pia.rest.unifiedaggregator.toUi
import com.siriusxm.pia.rest.unifiedaggregator.unifiedAggregatorClient
import com.siriusxm.smithy4kt.Model
import com.siriusxm.smithy4kt.SmithyModel
import unifiedcontent.aggregator.Aggregator
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.decodeFromJsonElement

/**
 * Global singleton that manages early loading of aggregator data.
 * This is initialized on app startup (after authentication) to pre-load
 * data before the user navigates to the aggregator section.
 *
 * This centralizes aggregator service management so that:
 * 1. Data loading starts immediately after authentication
 * 2. The service is available to any component that needs it
 * 3. AggregatorApplication can delegate to this loader
 */
object GlobalAggregatorLoader {
    private var initialized = false
    private lateinit var context: ApplicationContext
    private lateinit var scope: CoroutineScope
    private lateinit var app: AggregatorApplication

    /**
     * The aggregator SDK, available after initialize() is called.
     */
    lateinit var aggregator: Aggregator
        private set

    /**
     * The entity model, populated after data loading completes.
     */
    var model: Model? = null
        private set

    private val modelParser = Json {
        ignoreUnknownKeys = true
    }

    private var _aggregatorServiceState: EagerLoadedState<AggregatorService>? = null

    /**
     * Initialize the loader with the application context and scope.
     * Must be called once after authentication, before startLoading().
     *
     * @param app The aggregator application instance
     * @param context The application context
     * @param scope The coroutine scope for background loading
     */
    fun initialize(app: AggregatorApplication, context: ApplicationContext, scope: CoroutineScope) {
        if (initialized) return
        this.app = app
        this.context = context
        this.scope = scope

        this.aggregator = unifiedAggregatorClient(context).sdk

        _aggregatorServiceState = EagerLoadedState(scope) {
            loadAggregatorService()
        }
        initialized = true
    }

    /**
     * Start loading aggregator data in the background.
     * Safe to call multiple times; subsequent calls are no-ops if already loading/loaded.
     */
    fun startLoading() {
        check(initialized) { "GlobalAggregatorLoader must be initialized before startLoading()" }
        _aggregatorServiceState?.startLoading()
    }

    /**
     * Whether the loader has been initialized.
     */
    val isInitialized: Boolean get() = initialized

    /**
     * Whether loading is in progress.
     */
    val isLoading: Boolean get() = _aggregatorServiceState?.isLoading == true

    /**
     * Error that occurred during loading, if any.
     */
    val error: Throwable? get() = _aggregatorServiceState?.error

    /**
     * Get the current aggregator service value (may be null if not loaded yet).
     */
    fun value(): AggregatorService? = _aggregatorServiceState?.value()

    /**
     * Collect the aggregator service state in a composable.
     * This will start loading if not already started.
     */
    @Composable
    fun collect(): State<AggregatorService?> {
        return _aggregatorServiceState?.collect()
            ?: throw IllegalStateException("GlobalAggregatorLoader not initialized")
    }

    /**
     * Update the service with new data (used for partial reloads like producers/consumers).
     */
    fun update(service: AggregatorService) {
        _aggregatorServiceState?.update(service)
    }

    /**
     * Reload all aggregator data.
     */
    suspend fun reload() {
        _aggregatorServiceState?.reload()
    }

    /**
     * Internal method that loads all aggregator service data in parallel.
     */
    private suspend fun loadAggregatorService(): AggregatorService = coroutineScope {
        val types = async {
            // includeCounts populates EntityTypeConfiguration.approximateEntityCount from the
            // most recent nightly snapshot. Requested here, in the single global load, so that
            // the types table and the type detail page render the same number and cannot
            // disagree. Server-side the flag costs one extra item read for the whole response,
            // not one per type.
            aggregator.getEntityTypes(includeCounts = true).sortedAlphaBy { it.name.ifBlank { it.type } }
        }
        val relationships = async {
            aggregator.getRelationshipSchemas().map { it.toUi() }.sortedAlphaBy { it.name }
        }
        val producers = async {
            aggregator.getProducers().sortedAlphaBy {
                it.name?.takeIf(String::isNotBlank) ?: it.id
            }
        }
        val consumers = async {
            aggregator.getConsumers().sortedAlphaBy {
                it.name?.takeIf(String::isNotBlank) ?: it.id
            }
        }
        val partials = async {
            try {
                aggregator.getPartialSchemas().sortedAlphaBy {
                    it.name?.takeIf(String::isNotBlank) ?: it.id
                }
            } catch (_: Throwable) {
                emptyList()
            }
        }
        val modelJob = async {
            val modelJson = aggregator.getEntityModel()
            modelParser.decodeFromJsonElement<Model>(modelJson)
        }

        this@GlobalAggregatorLoader.model = modelJob.await()

        AggregatorService(
            app = app,
            reloader = { reload() },
            context = context,
            entityTypes = types.await(),
            relationships = relationships.await(),
            producers = producers.await(),
            consumers = consumers.await(),
            partials = partials.await(),
            entityModel = try {
                SmithyModel(modelJob.await())
            } catch (_: Throwable) {
                null
            }
        )
    }
}
